import React, { useCallback, useRef, useState } from "react";
import ReactSelect, { SingleValue, StylesConfig } from "react-select";

export interface LocationResult {
  placeId: number;
  displayName: string;
  lat: number;
  lon: number;
  address: {
    road?: string;
    suburb?: string;
    city?: string;
    town?: string;
    village?: string;
    county?: string;
    state?: string;
    postcode?: string;
    country?: string;
    countryCode?: string;
  };
  /** True when the user typed the address themselves (no coordinates available). */
  isCustom?: boolean;
}

interface Option {
  value: number;
  label: string;
  location: LocationResult;
}

interface LocationSearchProps {
  id?: string;
  value?: LocationResult | null;
  valueLabel?: string;
  onChange: (location: LocationResult | null) => void;
  placeholder?: string;
  error?: boolean;
  disabled?: boolean;
  className?: string;
  portal?: boolean;
  /** Offer "use what I typed" when the address isn't found by the search. */
  allowCustom?: boolean;
}

const NOMINATIM_URL = "https://nominatim.openstreetmap.org/search";

const styles: StylesConfig<Option> = {
  control: (base, { isFocused, isDisabled }) => ({
    ...base,
    border: "1px solid var(--bd)",
    borderRadius: "7px",
    boxShadow: isFocused ? "0 0 0 3px #e6f0f8" : "none",
    minHeight: "38px",
    height: "38px",
    fontSize: "13px",
    background: isDisabled ? "var(--bg, #f4f5f6)" : "var(--bg2)",
    cursor: isDisabled ? "not-allowed" : "pointer",
    opacity: 1,
    ":hover": { border: "1px solid var(--bd)" },
  }),
  option: (base, { isSelected }) => ({
    ...base,
    fontFamily: "system-ui, sans-serif",
    fontSize: "12px",
    overflowWrap: "anywhere" as const,
    backgroundColor: isSelected ? "var(--b)" : "transparent",
    color: isSelected ? "#fff" : base.color,
    cursor: "pointer",
    ":hover": {
      backgroundColor: isSelected
        ? "var(--b)"
        : "color-mix(in srgb, var(--b) 15%, transparent)",
    },
    ":active": { backgroundColor: "var(--b)", color: "#fff" },
  }),
  valueContainer: (base) => ({ ...base, height: "38px", padding: "0 8px" }),
  indicatorsContainer: (base) => ({ ...base, height: "38px" }),
  singleValue: (base) => ({
    ...base,
    fontSize: "12px",
    color: "var(--tx)",
  }),
  placeholder: (base) => ({
    ...base,
    fontSize: "12px",
    color: "var(--tx3, #909399)",
  }),
  input: (base) => ({
    ...base,
    fontSize: "12px",
    margin: 0,
    padding: 0,
    boxShadow: "none",
  }),
  menu: (base) => ({
    ...base,
    borderRadius: "7px",
    border: "1px solid var(--bd, #dde1e6)",
    boxShadow: "0 4px 18px rgba(0,0,0,0.1)",
    zIndex: 9999,
  }),
  menuList: (base) => ({
    ...base,
    padding: "4px 0",
    maxHeight: "220px",
    borderRadius: "7px",
  }),
  noOptionsMessage: (base) => ({ ...base, fontSize: "12px" }),
};

const LocationSearch = ({
  id,
  value = null,
  valueLabel = "",
  onChange,
  placeholder = "Search location…",
  error = false,
  disabled = false,
  className = "",
  portal = false,
  allowCustom = false,
}: LocationSearchProps) => {
  const [options, setOptions] = useState<Option[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const selectedOption: Option | null = value
    ? { value: value.placeId, label: value.displayName, location: value }
    : valueLabel
      ? { value: -1, label: valueLabel, location: null as never }
      : null;

  const search = useCallback((query: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim()) {
      setOptions([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          q: query,
          format: "json",
          addressdetails: "1",
          limit: "7",
        });
        const res = await fetch(`${NOMINATIM_URL}?${params}`, {
          headers: { "Accept-Language": "en" },
        });
        const data = await res.json();
        setOptions(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          data.map((item: any) => ({
            value: item.place_id,
            label: item.display_name,
            location: {
              placeId: item.place_id,
              displayName: item.display_name,
              lat: parseFloat(item.lat),
              lon: parseFloat(item.lon),
              address: {
                road: item.address?.road,
                suburb: item.address?.suburb,
                city: item.address?.city,
                town: item.address?.town,
                village: item.address?.village,
                county: item.address?.county,
                state: item.address?.state,
                postcode: item.address?.postcode,
                country: item.address?.country,
                countryCode: item.address?.country_code,
              },
            } satisfies LocationResult,
          })),
        );
      } catch {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 400);
  }, []);

  const typed = inputValue.trim();
  const menuOptions: Option[] =
    allowCustom && typed
      ? [
          ...options,
          {
            value: -2,
            label: `Use "${typed}" as the address`,
            location: {
              placeId: -1,
              displayName: typed,
              lat: 0,
              lon: 0,
              address: {},
              isCustom: true,
            },
          },
        ]
      : options;

  const handleChange = (opt: SingleValue<Option>) => {
    onChange(opt ? opt.location : null);
  };

  return (
    <ReactSelect<Option>
      classNamePrefix="rs"
      inputId={id}
      className={className}
      options={menuOptions}
      value={selectedOption}
      onChange={handleChange}
      onInputChange={(val, { action }) => {
        setInputValue(val);
        if (action === "input-change") search(val);
      }}
      placeholder={placeholder}
      isDisabled={disabled}
      isLoading={loading}
      isClearable
      filterOption={() => true}
      loadingMessage={() => "Loading..."}
      noOptionsMessage={({ inputValue }) =>
        inputValue ? "No locations found" : "Type to search"
      }
      menuPlacement="auto"
      menuPortalTarget={portal ? document.body : undefined}
      menuPosition={portal ? "fixed" : undefined}
      styles={{
        ...styles,
        control: (base, state) => ({
          ...(styles.control!(base, state) as object),
          border: error
            ? "1px solid var(--red, #a32d2d)"
            : state.isFocused
              ? "1px solid var(--b)"
              : "1px solid var(--bd)",
          boxShadow:
            error && state.isFocused
              ? "0 0 0 3px var(--red-bg, #fcebeb)"
              : state.isFocused
                ? "0 0 0 3px #e6f0f8"
                : "none",
        }),
      }}
    />
  );
};

export default LocationSearch;
